import React from "react";
import { Unschedule } from "../../models/Incidents";
import { PartnerDetailsUnschedule } from "./PartnerDetails.tsx";
import { useFormik } from "formik";
import {
  buildUnscheduleIncidentRecord,
  getUnscheduleBranchId,
  getUnscheduleBranchName,
} from "../../utils/incidentBranchMeta.ts";
import { resolveIncidentRealChecks } from "../../utils/incidentDisplayTimes.ts";
import { effectiveNightMetaForPartner } from "../../utils/nightScheduleEligibility.ts";
import type { BranchReportMeta } from "../../utils/payrollBranchMeta.ts";
import {
  IncidentReason,
  IncidentReasonSchema,
} from "../../validation/schemas/IncidentReasonSchema.ts";
import CustomTextArea from "../formik/CustomTextArea.tsx";
import { Chip, Tooltip } from "@mui/material";
import { notification } from "../../utils/Notifications.tsx";
import { saveUnscheduleIncident } from "../../services/incident.service.ts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { setRefetch } from "../../store/slices/incidenciaSlice.ts";
import type { IncidentAfterSavePayload } from "./incidentTableTypes.ts";
import {
  useIncidentRowExit,
  type IncidentRowExitVariant,
} from "./useIncidentRowExit.ts";

const formatDateLabel = (dateStr?: string) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    .replace(".", "");
};

const DEFAULT_BRANCH_META: BranchReportMeta = {
  nightScheduleEnabled: false,
  cutoffMinutes: 3 * 60,
};

const UnscheduleRow = ({
  unschedule,
  index,
  removeUnscheduleRecord,
  onAfterSave,
  branchMetaMap = {},
  maxNightCutoffMinutes,
  operationDate,
  showBranchName = false,
  branchNameById = {},
}: {
  unschedule: Unschedule;
  index: number;
  removeUnscheduleRecord: () => void;
  onAfterSave?: (payload: IncidentAfterSavePayload) => void;
  branchMetaMap?: Record<number, BranchReportMeta>;
  maxNightCutoffMinutes?: number;
  /** Día operativo de la consulta (YYYY-MM-DD). */
  operationDate?: string;
  showBranchName?: boolean;
  branchNameById?: Record<number, string>;
}) => {
  const dispatch = useDispatch();
  const { refetch, quickAnswer } = useSelector(
    (state: RootState) => state.incidencias
  );
  const [disabled, setDisabled] = React.useState(false);
  const isSubmittingRef = React.useRef(false);
  const { rowClassName, isExiting, playExitAndThen } = useIncidentRowExit();

  const branchId = getUnscheduleBranchId(unschedule);
  const branchLabel = showBranchName
    ? getUnscheduleBranchName(unschedule, branchNameById)
    : "";
  const branchMeta = branchMetaMap[branchId] ?? DEFAULT_BRANCH_META;
  const partnerShift = unschedule.partner?.candidate?.shift?.name;

  const incidentRecord = React.useMemo(
    () => buildUnscheduleIncidentRecord(unschedule, operationDate),
    [unschedule, operationDate]
  );

  const { checkIn: realCheckIn, checkOut: realCheckOut } = React.useMemo(
    () =>
      resolveIncidentRealChecks(incidentRecord, {
        branchMeta,
        shiftName: partnerShift,
        maxNightCutoffMinutes,
      }),
    [
      incidentRecord,
      branchId,
      branchMeta,
      partnerShift,
      maxNightCutoffMinutes,
    ]
  );

  const effectiveCheckIn = realCheckIn ?? unschedule.checkIn;
  const effectiveCheckOut = realCheckOut ?? unschedule.checkOut;
  const showNightIcon = effectiveNightMetaForPartner(
    branchMeta,
    partnerShift
  ).nightScheduleEnabled;

  const checkInDate = effectiveCheckIn?.checkDate;
  const checkOutDate = effectiveCheckOut?.checkDate;
  const isCrossDayCheck =
    Boolean(checkInDate) &&
    Boolean(checkOutDate) &&
    String(checkInDate) !== String(checkOutDate);

  const completeUnscheduleSave = (
    payload: IncidentAfterSavePayload,
    exitVariant: IncidentRowExitVariant,
    notifyMessage?: string
  ) => {
    if (notifyMessage) {
      notification("Info", notifyMessage, "info");
    }
    playExitAndThen(exitVariant, () => {
      if (onAfterSave) {
        onAfterSave(payload);
      } else {
        removeUnscheduleRecord();
        if (!notifyMessage) {
          dispatch(setRefetch(!refetch));
        }
      }
      setDisabled(false);
      isSubmittingRef.current = false;
    });
  };

  const formik = useFormik({
    validationSchema: IncidentReasonSchema,
    initialValues: IncidentReason,
    onSubmit: async (values) => {
      if (isSubmittingRef.current || disabled) return;
      
      
      if (!values.type) {
        notification("Error", "Debes seleccionar Aceptar o Rechazar", "danger");
        return;
      }
      
      isSubmittingRef.current = true;
      setDisabled(true);
      
      try {
        const { reason, type } = values;
        const data = {
          reason: reason || "",
          status: "No programado",
          checkIn: effectiveCheckIn?.id ?? unschedule?.checkIn?.id ?? -1,
          checkOut: effectiveCheckOut?.id ?? unschedule?.checkOut?.id ?? -1,
          type,
        };

        const response = await saveUnscheduleIncident(data);

        const payload: IncidentAfterSavePayload = {
          kind: "unschedule",
          partnerId: unschedule.partner.id,
          checkInId: unschedule.checkIn.id,
          unschedule,
          type: values.type,
          reason: values.reason || "",
          newScheduleId: response?.scheduleId,
          dayClose: response?.dayClose ?? null,
        };

        const exitVariant: IncidentRowExitVariant =
          type === "Rechazar" ? "reject" : "accept";

        if (response?.message === "Ya existe una incidencia") {
          console.warn("⚠️ Incidencia no programada ya existe en backend, ocultando de la tabla");
          completeUnscheduleSave(
            payload,
            exitVariant,
            "La incidencia ya fue procesada anteriormente"
          );
          return;
        }

        completeUnscheduleSave(payload, exitVariant);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error al guardar incidencia";
        
        
        if (errorMessage.includes("Ya existe") || errorMessage.includes("ya existe")) {
          console.warn("⚠️ Error: Incidencia no programada ya existe, ocultando de la tabla:", errorMessage);
          const exitVariant: IncidentRowExitVariant =
            values.type === "Rechazar" ? "reject" : "accept";
          completeUnscheduleSave(
            {
              kind: "unschedule",
              partnerId: unschedule.partner.id,
              checkInId: unschedule.checkIn.id,
              unschedule,
              type: values.type,
              reason: values.reason || "",
            },
            exitVariant,
            "La incidencia ya fue procesada anteriormente"
          );
          return;
        }

        notification("Error", errorMessage, "danger");
        setDisabled(false);
        isSubmittingRef.current = false;
        return;
      }
    },
  });

  return (
    <tr
      className={`text-center${rowClassName ? ` ${rowClassName}` : ""}`}
      style={{
        backgroundColor: index % 2 === 0 ? "white" : "#f8f9fa",
        transition: isExiting ? "none" : "all 0.2s ease",
        borderBottom: "1px solid #dee2e6",
      }}
      onMouseEnter={(e) => {
        if (!isExiting) {
          e.currentTarget.style.backgroundColor = "#e9ecef";
        }
      }}
      onMouseLeave={(e) => {
        if (!isExiting) {
          e.currentTarget.style.backgroundColor =
            index % 2 === 0 ? "white" : "#f8f9fa";
        }
      }}
    >
      <td
        style={{
          padding: "6px 8px",
          fontSize: "12px",
          borderLeft: "none",
          borderRight: "none",
          backgroundColor: "transparent",
          textAlign: "center",
        }}
      >
        <PartnerDetailsUnschedule
          record={unschedule}
          showNightIcon={showNightIcon}
          branchName={branchLabel || undefined}
        />
      </td>
      <td
        style={{
          padding: "6px 8px",
          borderLeft: "none",
          borderRight: "none",
          backgroundColor: "transparent",
        }}
      />
      <td
        style={{
          padding: "6px 8px",
          borderLeft: "none",
          borderRight: "none",
          backgroundColor: "transparent",
        }}
      />
      <td
        style={{
          padding: "6px 8px",
          fontSize: "12px",
          fontWeight: "bold",
          color: "#f0ad4e",
          borderLeft: "none",
          borderRight: "none",
          backgroundColor: "transparent",
        }}
      >
        <div>{effectiveCheckIn?.checkTime ?? "--"}</div>
      </td>
      <td
        style={{
          padding: "6px 8px",
          fontSize: "12px",
          fontWeight: "bold",
          color: "#f0ad4e",
          borderLeft: "none",
          borderRight: "none",
          backgroundColor: "transparent",
        }}
      >
        <div>{effectiveCheckOut?.checkTime ?? "--"}</div>
        {isCrossDayCheck && checkOutDate && (
          <div style={{ fontSize: "10px", fontWeight: 500, color: "#adb5bd" }}>
            {formatDateLabel(checkOutDate)}
          </div>
        )}
      </td>

      <td
        style={{
          padding: "6px 8px",
          borderLeft: "none",
          borderRight: "none",
          backgroundColor: "transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="w-100">
            <p
              className="w-100 mt-1 fs-12px fw-800 bg mb-2"
              style={{
                color: "#6c757d",
                marginBottom: "6px",
                fontSize: "12px",
              }}
            >
              <i
                className="fa fa-clock-rotate-left me-2"
                style={{ fontSize: "11px", color: "#6c757d" }}
              ></i>
              No programado
            </p>
            {(() => {
              const detectedRaw =
                (unschedule as any).detectedAt ||
                (effectiveCheckIn as any)?.createdAt ||
                (effectiveCheckIn?.checkDate && effectiveCheckIn?.checkTime
                  ? `${String(effectiveCheckIn.checkDate).slice(0, 10)}T${String(effectiveCheckIn.checkTime).slice(0, 8)}`
                  : null);
              if (!detectedRaw) return null;
              const detected = new Date(detectedRaw);
              if (Number.isNaN(detected.getTime())) return null;
              const label = detected
                .toLocaleString("es-MX", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace(".", "");
              return (
                <p
                  style={{
                    fontSize: "10px",
                    color: "#adb5bd",
                    marginBottom: "6px",
                    fontWeight: 500,
                  }}
                >
                  Detectada: {label}
                </p>
              );
            })()}
            <div style={{ width: "100%" }}>
              <textarea
                disabled={disabled}
                className="form-control"
                style={{
                  fontSize: "12px",
                  borderRadius: "6px",
                  border:
                    formik.touched.reason && formik.errors.reason
                      ? "1px solid #dc3545"
                      : "1px solid #e9ecef",
                  padding: "6px 10px",
                  minHeight: "45px",
                  resize: "vertical",
                  transition: "all 0.2s ease",
                  backgroundColor: disabled ? "#f8f9fa" : "white",
                  color: disabled ? "#6c757d" : "#212529",
                }}
                id="reason"
                name="reason"
                placeholder="Escribe la razón de la incidencia..."
                onChange={formik.handleChange}
                value={formik.values.reason}
                onFocus={(e) => {
                  if (formik.touched.reason && formik.errors.reason) {
                    e.target.style.borderColor = "#dc3545";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(220, 53, 69, 0.1)";
                  } else {
                    e.target.style.borderColor = "#50B7C0";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(80, 183, 192, 0.1)";
                  }
                }}
                onBlur={(e) => {
                  if (formik.touched.reason && formik.errors.reason) {
                    e.target.style.borderColor = "#dc3545";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(220, 53, 69, 0.1)";
                  } else {
                    e.target.style.borderColor = "#e9ecef";
                    e.target.style.boxShadow = "none";
                  }
                  formik.handleBlur(e);
                }}
                maxLength={200}
              />
              {formik.touched.reason && formik.errors.reason && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px",
                    marginLeft: "4px",
                  }}
                >
                  {formik.errors.reason}
                </div>
              )}
            </div>
          </div>
          <div
            className="w-100 mt-1 fs-12px fw-800 bg gap-2 d-flex justify-content-center align-items-center flex-wrap"
            style={{ maxWidth: "100%", marginTop: "4px" }}
          >
            {quickAnswer.map((item: string, index: number) => (
              <Chip
                label={item}
                key={index}
                size="small"
                style={{ cursor: "pointer", fontSize: "10px", height: "22px" }}
                onClick={() => formik.setFieldValue("reason", item)}
              />
            ))}
          </div>
        </div>
      </td>
      <td
        style={{
          padding: "6px 8px",
          backgroundColor: "transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "6px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Tooltip title="Aceptar (Pagable)" arrow>
            <button
              type="button"
              className="incidents-btn-action incidents-btn-aceptar"
              onClick={async () => {
                await formik.setFieldValue("type", "Aceptar", false);
                formik.handleSubmit();
              }}
              disabled={disabled}
                style={{
                  padding: "6px 8px",
                  fontSize: "14px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: "#50B7C0",
                  color: "white",
                  cursor: disabled ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(80, 183, 192, 0.2)",
                  opacity: disabled ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 8px rgba(80, 183, 192, 0.3)";
                  e.currentTarget.style.backgroundColor = "#3A9BA3";
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 4px rgba(80, 183, 192, 0.2)";
                  e.currentTarget.style.backgroundColor = "#50B7C0";
                }
              }}
            >
              <i className="fa-solid fa-check"></i>
            </button>
          </Tooltip>

          <Tooltip title="Rechazar (No pagable)" arrow>
            <button
              type="button"
              className="incidents-btn-action incidents-btn-rechazar"
              onClick={async () => {
                await formik.setFieldValue("type", "Rechazar", false);
                formik.handleSubmit();
              }}
              disabled={disabled}
                style={{
                  padding: "6px 8px",
                  fontSize: "14px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: "#DC2C27",
                  color: "white",
                  cursor: disabled ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(220, 44, 39, 0.2)",
                  opacity: disabled ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 8px rgba(220, 44, 39, 0.3)";
                  e.currentTarget.style.backgroundColor = "#B82420";
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 4px rgba(220, 44, 39, 0.2)";
                  e.currentTarget.style.backgroundColor = "#DC2C27";
                }
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
};

export default UnscheduleRow;
